
import * as SurveyCore from 'survey-core';
import {
  ClearFilesEvent,
  CurrentPageChangedEvent,
  JsonObject,
  Model,
  QuestionFileModel,
  QuestionSignaturePadModel,
  surveyLocalization,
  UploadFilesEvent,
} from 'survey-core';
import * as widgets from 'surveyjs-widgets';
import { Survey } from 'survey-vue-ui';
import 'survey-core/defaultV2.min.css';
import theme from './theme';
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import Toasted from 'vue-toasted';
import { extractError, initClient } from '../../api';
import { WindowType } from '../../util';
import { Media, SurveyJsUiClient, SurveyResultValidityCheck, SurveyResultView, SurveyUpdateRequest, SurveyView } from '../backend/backendapi';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import 'survey-core/survey.i18n.js';
import { fixSlLanguage } from './langFixSl';
import { GuestInfo } from './models';
import { CookieUtils } from './utils';
import { applyCustomDropdownChoices, registerSurveyCustomDropdownProperties } from './customDropdown';
import { applyCustomDownload, registerSurveyCustomDownloadProperties } from './customDownload';
import { applyCustomImageVideo,registerSurveyCustomImageVideoProperties } from './customImageVideo';
import { applyCustomTelValidator } from './customTelValidator';
import { applyCookieBehavior, isCookieForExitInfo, returnMinutesByCookie } from './cookieBehavior';
import { registerSimpleCustomProperties } from './customPropsRegistration';
import { supportedLangSet } from './lang';
import { fixSignaturePad } from './fixSignaturePad';
import Spinner from '../util/Spinner';
import CenterOnParent from '../util/CenterOnParent';

applyCookieBehavior();

widgets.select2(SurveyCore);
widgets.inputmask(SurveyCore);
widgets.jquerybarrating(SurveyCore);
widgets.jqueryuidatepicker(SurveyCore);
widgets.nouislider(SurveyCore);
widgets.select2tagbox(SurveyCore);
widgets.sortablejs(SurveyCore);
widgets.ckeditor(SurveyCore);
widgets.autocomplete(SurveyCore);
widgets.bootstrapslider(SurveyCore);

JsonObject
    .metaData
    .addProperty('question', { name: 'score:number' });

declare var window: WindowType;

surveyLocalization.supportedLocales = supportedLangSet;
Vue.use(Toasted, { duration: 2000, iconPack: 'fontawesome' });

export type SurveyUser = {
  firstName: string,
  lastName: string,
  email: string,
  company: string,
}

fixSlLanguage();
fixSignaturePad();

@Component({
  name: 'SurveyJSComponent',
  components: {
    CenterOnParent,
    Spinner,
    Survey,
    vSelect,
  },
})
export default class SurveyJSComponent extends Vue {
  @Prop({ required: true })
  readonly surveyId!: string;
  @Prop({ required: true })
  readonly started!: Date;
  @Prop({ default: null })
  readonly user!: SurveyUser | null;
  @Prop({ default: null })
  readonly completeScreenBackButtonAction!: (() => void) | null;
  @Prop({ default: null })
  readonly completeScreenBackButtonText!: string | null;
  private nextSurveyId: string | null = null; // Store the next survey ID

  private surveyview!: SurveyView;
  private surveyJson!: string;
  private survey: Model = new Model();
  loading: boolean = false;
  errorLoading: boolean = false;
  private isAnon: boolean = false;
  private selectedLanguageProp: string = 'en'; //do not access; use selectedLanguage
  readonly languages: { locale: string, text: string }[] = [];
  private isCustomSurveyMailRequired: boolean = true;
  private rememberCookieDueSeconds: number = 0;
  private backDueSeconds: number = 0;
  isUserCheckOverride: boolean = false;
  private isKiosk: boolean = false;
  private trackingId: string | undefined = undefined;
  userCheck: SurveyUpdateRequest = new SurveyUpdateRequest();
  private addedMedia: Media[] = [];

  private readonly api = initClient(SurveyJsUiClient);

  get isSearchButtonDisabled(): boolean {
    return !this.userCheck.firstname || !this.userCheck.lastname;
  }

  get selectedLanguage(): string {
    return this.selectedLanguageProp;
  }

  set selectedLanguage(val: string) {
    this.selectedLanguageProp = val;
    this.survey.locale = val;
  }

  mounted(): void {
    surveyLocalization.defaultLocale = undefined as any;
    surveyLocalization.currentLocale = this.$i18n.locale;
    this.selectedLanguage = this.$i18n.locale;
    this.reloadAll();
  }

  private async reloadAll(): Promise<void> {
    this.loading = true;
    this.errorLoading = false;
    try {
      let langSelectStyle = document.getElementById('lang-selector')?.style;
      if (langSelectStyle) langSelectStyle.display = 'none';
      const paramsString = window.location.search;
      const searchParams = new URLSearchParams(paramsString);
      // Iterating the search parameters and set flags
      for (const p of searchParams) {
        if (searchParams.has('kiosk')) { // set kiosk mode urlparam
          this.isKiosk = true;
        }
        if (searchParams.has('rsec')) { // set remember time by urlparam
          let strDueSec = searchParams.get('rsec');
          strDueSec ??= '0';
          this.rememberCookieDueSeconds = parseInt(decodeURIComponent(strDueSec));
        }
        if (searchParams.has('exitcheck')) { // set exitcheck time by urlparam
          this.isUserCheckOverride = true;
          this.loading = false;
        }
        if (searchParams.has('backs')) { // set back jump time by urlparam
          let strBackSec = searchParams.get('backs');
          strBackSec ??= '0';
          this.backDueSeconds = parseInt(decodeURIComponent(strBackSec));
        }
        if (searchParams.has('tid')) { // set tracking identifier by urlparam
          let trackingId = searchParams.get('tid');
          trackingId ??= '0';
          this.trackingId = decodeURIComponent(trackingId);
        }
        // Capture nextid parameter
        if (searchParams.has('nextid')) {
          this.nextSurveyId = searchParams.get('nextid');
        }
      }

      this.addedMedia.splice(0);
      this.surveyview = (await this.api.getSurvey(this.surveyId));
      let json = this.surveyJson = this.surveyview.json;

      // Time validation for runnable time set in survey json, if not in runnable time show toast and do not load survey
      if (!this.isWithinRunnableTimeAtStart(json)) {
        this.exitToast('survey.notAvailableAtThisTime');
        this.loading = false;
        return;
      }

      if (!window.application.loggedIn && !this.user) {
        let guestFromCookie: GuestInfo | undefined = undefined;
        try {
          const cookieContent = CookieUtils.tryGetCookie('revocheck.quicksurvey');
          if (cookieContent !== undefined) {
            guestFromCookie = JSON.parse(decodeURIComponent(cookieContent));
          }
        } catch (e) {
          guestFromCookie = undefined;
        }

        json = this.addAnonymousQuestions(json, guestFromCookie);
        if (CookieUtils.hasCookie('exitinfo', 'true')) {
          if (!this.isUserCheckOverride && !this.isKiosk) this.exitToast();
          CookieUtils.releaseCookie('exitinfo');
          this.checkAndDoBack();
        }
      }
      // register them for model usage otherwise they are not available in the model
      // DO NOT load translations of the designer here, otherwise it will break the surveyjs
      registerSimpleCustomProperties();
      registerSurveyCustomDropdownProperties();
      registerSurveyCustomDownloadProperties();
      registerSurveyCustomImageVideoProperties();
      this.survey = new Model(json);
      this.survey.locale = this.selectedLanguage;

      if (this.surveyview.hasContacts) {
        applyCustomDropdownChoices(this.survey, this.surveyview.contacts ?? []);
      }
      applyCustomDownload(this.survey, this.$t('general.download').toString());
      applyCustomImageVideo(this.survey);
      applyCustomTelValidator(this.survey);

      this.survey.onComplete.add(sender => {
        this.onComplete(sender.data);
      });
      if (this.completeScreenBackButtonAction) {
        const backButtonFunctionName = this.initBackButtonFunction();
        this.survey.onProcessHtml.add((sender, options) => {
          if (options.reason === 'completed') {
            if (!options.html) {
              options.html = '';
            }
            options.html +=
                '<div style="margin: 40px 0; width:100%">' +
                '    <button style="margin: auto" ' +
                '            class="btn btn-primary v2-class---button v2-class---button--primary v2-class---button--large" ' +
                '            onclick="' + backButtonFunctionName + '()">' +
                '        <span class="v2-class---button__text v2-class---button__text--secondary v2-class---button__text--large">' +
                '             ' + (this.completeScreenBackButtonText ?? this.$t('general.back')) + '' +
                '        </span>' +
                '    </button>' +
                '</div>';
          }
        });
      }
      this.survey.css = { navigationButton: 'btn btn-primary m-1 ' + this.survey.css.navigationButton };
      this.survey.applyTheme(theme);
      this.applyFileUploadStuff(this.survey);
      this.applyResultStillValidStuff();

      this.languages.splice(0);
      this.languages.push(...this.survey.getUsedLocales().filter(e => e).map((e: string) => ({ locale: e, text: this.$t(e).toString() })));
      //default lang
      if (!this.languages.some(e => e.locale === this.$i18n.locale)) {
        this.languages.push({ locale: this.$i18n.locale, text: 'default lang for<' + this.$t(this.$i18n.locale).toString() + '>' });
      } else {
        this.languages.push({ locale: 'default', text: 'default lang for<' + this.$t(this.$i18n.locale).toString() + '>' });
      }
      if (this.isUserCheckOverride) {
        this.$toasted.info('Exit/Verlassen/Izhod/Kilépés/Izlaz');
        return;
      }
    } catch (ex) {
      this.errorLoading = true;
    }
    this.loading = false;
  }

  private applyFileUploadStuff(survey: Model): void {
    survey.pages.forEach((page) => {
      page.questions.forEach((question) => {
        if (question instanceof QuestionFileModel) {
          (question as QuestionFileModel).storeDataAsText = false;
          (question as QuestionFileModel).waitForUpload = true;
        } else if (question instanceof QuestionSignaturePadModel) {
          (question as QuestionSignaturePadModel).storeDataAsText = false;
          (question as QuestionSignaturePadModel).waitForUpload = true;
        }
      });
    });
    survey.onUploadFiles.add((_: Model, options: UploadFilesEvent) => {
      Promise
          .allSettled(options.files.map(file => {
            return this.api.createMedia(this.surveyId, true, { data: new Blob([file], { type: file.type }), fileName: file.name })
                .then(e => {
                  this.addedMedia.push(e);
                  return ({ content: e.publicUrl, file: file });
                })
                .catch(e => extractError(e));
          }))
          .then((results) => {
            const ok = results.filter(e => e.status === 'fulfilled').map(e => e as PromiseFulfilledResult<any>).map(e => e.value);
            const failed = results.filter(e => e.status === 'rejected').map(e => e as PromiseRejectedResult).map(e => e.reason);
            options.callback(ok, failed);
            if (failed) this.$toasted.error(failed.join(' '));
          });
    });
    survey.onClearFiles.add(async (_: Model, options: ClearFilesEvent) => {
      if (!options.value) {//onClearFiles is always called when a new file is chosen in FileQuestion, even if there is no file to clear
        options.callback('success', options.value);
      }

      const media = this.addedMedia.find(e => e.publicUrl == options.value);
      if (!media) {
        return options.callback('error', 'Media not found');
      }

      try {
        await this.api.deleteMedia(this.surveyId, media.id!);

        const index = this.addedMedia.findIndex(e => e.publicUrl == options.value);
        if (index >= 0) {
          this.addedMedia.splice(index, 1);
        }
        options.callback('success', options.value);
      } catch (error) {
        return options.callback('error', extractError(error));
      }
    });
  }

  private applyResultStillValidStuff(): void {
    if (!window.application.loggedIn && !this.user) {
      this.survey.onCurrentPageChanged.add((survey: Model, options: CurrentPageChangedEvent) => {
        if (options.isNextPage && survey.pages.length && survey.pages[0] === options.oldCurrentPage) {
          this.checkResultStillValid();
        }
      });
    } else {
      this.checkResultStillValid();
    }
  }

  private async onComplete(results: any): Promise<void> {
    let result = new SurveyResultView();
    result.name = this.surveyview.name;
    result.surveyId = this.surveyview.id;
    result.startDate = this.started;
    result.isExitMark = false;
    result.rememberCookieDueSeconds = this.rememberCookieDueSeconds;
    // defaults
    result.participantFirstname = 'NA';
    result.participantLastname = 'NA';
    result.participantEmail = 'NA';
    // set tracking id if available
    if (this.trackingId) {
      results.trackingId = this.trackingId;
    }

    if (this.user) {
      result.participantFirstname = this.user.firstName;
      result.participantLastname = this.user.lastName;
      result.participantEmail = this.user.email;
      result.participantCompany = this.user.company;
    } else if (!window.application.loggedIn) {
      if (!this.isAnon) {
        result.participantFirstname = results.anonymousFirstName;
        result.participantLastname = results.anonymousLastName;
        result.participantEmail = this.isCustomSurveyMailRequired ? results.anonymousEmail : 'NA';
        result.participantCompany = results.anonymousCompany;
      }
      delete results.anonymousFirstName;
      delete results.anonymousLastName;
      delete results.anonymousEmail;
      delete results.anonymousCompany;
    }
    result.jsonResult = JSON.stringify(results);
    result.score = this.evaluate(results);
    let cookieName = this.survey.cookieName;
    if (cookieName !== null && cookieName.length > 0 && isCookieForExitInfo(cookieName)) {
      let minutes = 0;
      let minutesConfig = 0;
      minutes = returnMinutesByCookie(cookieName);
      minutesConfig = this.getCustomFutureMarkDoneDate(this.surveyJson);
      let expireDate = this.getEndTime(minutes < minutesConfig ? minutesConfig : minutes);
      result.doneDate = expireDate; // set doneDate in the future
      result.isExitMark = true;
      result.isKiosk = this.isKiosk;
    }
    result.uploadedMediaIds = this.addedMedia.map(e => e.id!);

    try {
      //save
      result = await this.api.saveResult(this.surveyId, result);
      this.$toasted.info(this.$t('txtSaved').toString());

      //passed/failed toast
      if (JSON.parse(this.surveyJson).customShowScoreResultOnComplete) {
        if (result.isPassed) {
          this.$toasted.success(this.$t('survey.onComplete.passed').toString());
        } else {
          this.$toasted.error(this.$t('survey.onComplete.failed').toString());
        }
      }

      // Redirect to next survey if nextid is set
      if (this.nextSurveyId) {
        this.redirectToNextSurvey();
        return; // Skip the back action if redirecting to next survey
      }

      //automatic back action
      this.checkAndDoBack();
    } catch (e) {
      this.$toasted.error(this.$t('txtError') + ': ' + JSON.stringify(e));
    }
  }

  private onUpdateDoneDate(request: SurveyUpdateRequest): void {
    this.api.updateResultEnd(request)
        .then(_ => {
          this.isUserCheckOverride = false;
          this.exitToast();
          this.checkAndDoBack();
        })
        .catch(e => {
          if (e.status === 404) {
            this.$toasted.error(this.$t('txtError') + ': ' + this.$t('errorNotFound'));
          } else {
            this.$toasted.error(this.$t('txtError') + ': ' + this.$t(JSON.stringify(e)));
          }
        });
  }

  private getEndTime(minutes) {
    const date = new Date();
    date.setTime(date.getTime() + (minutes * 60 * 1000));
    date.setUTCMinutes(0);
    date.setUTCSeconds(0);
    date.setMilliseconds(0);
    return date;
  }

  private addAnonymousQuestions(json: string, guest: GuestInfo | undefined): string {
    const obj = JSON.parse(json);
    if (obj.customSurveyNoInfoLink === true) { // hide survey nav info
      const element = document.getElementById('infosurvey');
      if (element != undefined) {
        element.hidden = true;
      }
    }
    if (obj.customSurveyNoMail === true) {
      this.isCustomSurveyMailRequired = false;
    }
    if (obj.customSurveyPropAnon === true) { // is anon survey, we return without adding page
      this.isAnon = true;
      return json;
    }
    if (!obj.customRemember) { // remember contact is not activated
      guest = undefined;
    }
    const locales = new Model(obj).getUsedLocales().map(e => e ?? '');
    let page = {
      'name': 'page0',
      'title': Object.assign({}, ...locales.map(l => ({ [l]: this.$t('surveyUserdata', l).toString() }))),
      'elements': [
        {
          'type': 'text',
          'name': 'anonymousFirstName',
          'title': Object.assign({}, ...locales.map(l => ({ [l]: this.$t('firstname', l).toString() }))),
          'isRequired': true,
          'defaultValue': guest?.first ?? '',
        },
        {
          'type': 'text',
          'name': 'anonymousLastName',
          'title': Object.assign({}, ...locales.map(l => ({ [l]: this.$t('lastname', l).toString() }))),
          'isRequired': true,
          'defaultValue': guest?.last ?? '',
        },
        {
          'type': 'text',
          'name': 'anonymousEmail',
          'title': Object.assign({}, ...locales.map(l => ({ [l]: this.$t('email', l).toString() }))),
          'isRequired': this.isCustomSurveyMailRequired,
          'defaultValue': guest?.mail ?? '',
          'autoComplete': 'email',
          'validators': [
            {
              'type': 'email',
            },
          ],
        },
        {
          'type': 'text',
          'name': 'anonymousCompany',
          'title': Object.assign({}, ...locales.map(l => ({ [l]: this.$t('survey.take.anonymous.company', l).toString() }))),
          'isRequired': false,
          'defaultValue': guest?.com ?? '',
        },
      ],
    };
    obj['pages'] = [page].concat(obj['pages'] ?? []);
    return JSON.stringify(obj);
  }

  private evaluate(results: any): number {
    return (JSON.parse(this.surveyJson).pages as any[] || []).map(p => (p.elements as any[] || []).map(e => {
      if (!e.score) {
        return 0;
      }

      let actual = JSON.stringify(results[e.name]);
      let expected = JSON.stringify(e.correctAnswer);
      return actual == expected ? e.score : 0;
    }).reduce((l, r) => l + r, 0)).reduce((l, r) => l + r, 0);
  }

  onCheck() {
    this.userCheck.id = this.surveyId;
    this.onUpdateDoneDate(this.userCheck);
  }

  private initBackButtonFunction(): string {
    const id = crypto.randomUUID().replace(/-/g, '');
    const surveyBackButtonFunctionName = 'surveyBackButton' + id;
    window[surveyBackButtonFunctionName] = () => {
      this.completeScreenBackButtonAction!();
    };
    return surveyBackButtonFunctionName;
  }

  // show large full screen toast and block background
  private exitToast(translation: string = 'surveyInfoExit') {
    this.$toasted.info('<br><br>' + this.$t(translation).toString() + '<br><br>',
        {
          icon: 'check',
          duration: 100000, position: 'bottom-center', containerClass: 'exitcontainertoast', className: 'exitclasstoast',
          type: 'info', fullWidth: true, fitToScreen: true, action: { text: 'x', onClick: (_, t) => t.goAway(1) },
        });
  }

  private checkAndDoBack() {
    if (this.backDueSeconds !== 0)// jump back after URL param seconds and not already in links
      setTimeout(() => {
        history.back();
      }, this.backDueSeconds * 1000);
  }

  private async checkResultStillValid(): Promise<void> {
    let email = '';
    if (this.user) {
      email = this.user.email;
    } else if (window.application.loggedIn) {
      email = window.application.user.userName ?? '';
    } else {
      if (!this.isAnon) {
        email = this.survey.data.anonymousEmail;
      }
    }
    if (email && JSON.parse(this.surveyJson).customShowStillValidToast) {
      const stillValid = await this.api.checkResultValid(this.surveyId, SurveyResultValidityCheck.fromJS({ email: email }));
      if (stillValid) {
        this.$toasted.info(this.$t('survey.result.stillValid').toString(), { duration: 5_000 });
      }
    }
  }

  // checks if current time is within runnable time set in survey json, if not show toast and do not load survey
  // checks only start time, if time limit is needed set surveyjs timer to a certain time periode
  private isWithinRunnableTimeAtStart(jsonInput: string): boolean {
    let json: any;
    try {
      json = JSON.parse(jsonInput);
    } catch (e) {
      console.error('Failed to parse Survey JSON', e);
      return true; // Fallback: allow if parsing fails
    }
    // Get current time in minutes from midnight
    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();

    const startTime = json.customRunnableTimeStart || '00:00';
    const stopTime = json.customRunnableTimeStop || '23:59';

    // Helper to convert "HH:mm" to total minutes
    const toMinutes = (timeStr: string) => {
      const [hrs, mins] = timeStr.split(':').map(Number);
      return hrs * 60 + mins;
    };

    const startMinutes = toMinutes(startTime);
    const stopMinutes = toMinutes(stopTime);

    // Comparison logic
    // Handles normal range (08:00 - 17:00) 
    // and overnight range (22:00 - 04:00)
    if (startMinutes <= stopMinutes) {
      return currentMinutes >= startMinutes && currentMinutes <= stopMinutes;
    } else {
      // Overlap midnight logic
      return currentMinutes >= startMinutes || currentMinutes <= stopMinutes;
    }
  }

  private getCustomFutureMarkDoneDate(jsonInput: string): number {
    let json: any;
    try {
      json = JSON.parse(jsonInput);
    } catch (e) {
      console.error('Failed to parse Survey JSON', e);
      return 60; // Fallback: allow if parsing fails
    }
    let customFutureMarkDoneDate = json.customFutureMarkDoneDate || 60 * 8; // default 8 hours if undefined
    if (customFutureMarkDoneDate <= 59) customFutureMarkDoneDate = 60 * 2; // if set to less than 1 hour, set to default 2 hours to avoid issues with very short times
    return customFutureMarkDoneDate;
  }

  // Method to redirect to the next survey
  private redirectToNextSurvey(): void {
    const pathParts = window.location.pathname.split('/');
    pathParts[pathParts.length - 1] = this.nextSurveyId!;
    const nextUrl = pathParts.join('/');
    
    // Small delay to allow the "Saved" toast to be visible
    setTimeout(() => {
      window.location.href = nextUrl;
    }, 1000);
  }
}
